import { memo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ClipType } from '@air/api/types';
import { usePlayerStore } from '@air/component-player';
import { useBreakpointsContext } from '@air/provider-media-query';

import { AnnotationToolSelector } from '~/components/AnnotationsV2/components/AnnotationToolSelector';
import { useAnnotationsToggle } from '~/components/AnnotationsV2/hooks/useAnnotationsToggle';
import { useCapturedCommentTimestamp } from '~/components/AnnotationsV2/hooks/useCapturedCommentTimestamp';
import { useCommentTimeMode } from '~/components/AnnotationsV2/hooks/useCommentTimeMode';
import { useEnableCommentRange } from '~/components/AnnotationsV2/hooks/useEnableCommentRange';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { formatCommentTimeLabel } from '~/components/AnnotationsV2/shared/formatCommentTimeLabel';
import { isTimestampableClipType } from '~/components/AnnotationsV2/shared/isTimestampableClipType';
import {
  DiscussionForm,
  type DiscussionFormHandle,
  type MentionsData,
} from '~/components/DiscussionsV2/DiscussionForm';
import { NewDiscussionActions } from '~/components/DiscussionsV2/InitialCommentInput/components/NewDiscussionActions';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useNoPreviewInVisualizer } from '~/hooks/useNoPreviewInVisualizer';
import { centralizedClipTypeSelector } from '~/store/centralizedClip/selectors';
import { isMobileAgent } from '~/utils/PlatformHelpers';

export type InitialCommentInputSubmitArgs = {
  comment: string;
  timestamp?: number | null;
  endTimestamp?: number;
};

export type InitialCommentInputProps = {
  /**
   * This is used for the edit state, if this prop exists, the form will
   * populate with the existing comment value.
   */
  comment?: string;

  /**
   * Determines whether or not the cancel button is visible next to the submit button,
   */
  hasCancelButton?: boolean;

  /**
   * An array of users that will populate the mentions menu
   * when a user types the trigger `@`.
   * @see https://github.com/signavio/react-mentions
   */
  mentionsList: MentionsData[];

  /**
   * Whether the mentions data is still loading from the API. When true, the
   * mention trigger button is disabled so react-mentions cannot snapshot an
   * empty `data` array at trigger time.
   */
  isLoadingMentions?: boolean;

  /**
   * onCancel event that fires when the user clicks on the cancel button.
   */
  onCancel?: () => void;

  /**
   * onSubmit event that fires when the user clicks on the submit button.
   */
  onSubmit: (args: InitialCommentInputSubmitArgs) => void;

  /**
   * The placeholder text for the discussions form input field.
   */
  placeholder?: string;

  shouldShowMentionsButton?: boolean;
  hasInternalAvatar?: boolean;
  canAnnotateClip: boolean;
  onEnterCommentMode?: () => void;
};

export const InitialCommentInput = memo(
  ({
    comment,
    hasCancelButton,
    isLoadingMentions,
    mentionsList,
    onSubmit,
    onCancel,
    placeholder,
    shouldShowMentionsButton,
    hasInternalAvatar,
    canAnnotateClip,
    onEnterCommentMode,
  }: InitialCommentInputProps) => {
    const commentRef = useRef<DiscussionFormHandle>(null);
    const { videoTimestamp: hasVideoTimestampFlag } = useFeatureFlags();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const videoElement = usePlayerStore((state) => state.videoElement);
    const { hideAnnotations, isAnnotationsEnabled } = useAnnotationsToggle();
    const clipType = useSelector(centralizedClipTypeSelector);
    const commentRange = useAnnotationV2Store((state) => state.commentRange);
    const setCommentRange = useAnnotationV2Store((state) => state.setCommentRange);
    const setCommentRangeEnabled = useAnnotationV2Store((state) => state.setCommentRangeEnabled);
    const shouldEnableTimestamps = useAnnotationV2Store((state) => state.shouldEnableCommentTimestamps);
    const setShouldEnableCommentTimestamps = useAnnotationV2Store((state) => state.setShouldEnableCommentTimestamps);
    const registerCommentInputFocusHandler = useAnnotationV2Store((state) => state.registerCommentInputFocusHandler);
    const { noPreviewInVisualizer } = useNoPreviewInVisualizer();
    const { captureCommentTimestamp, capturedTimestamp } = useCapturedCommentTimestamp({
      enabled: shouldEnableTimestamps,
    });
    const { enableCommentRange } = useEnableCommentRange();

    const isDesktop = isAboveMediumScreen && !isMobileAgent;
    const isVideo = clipType === ClipType.video;
    const isTimestampableClip = isTimestampableClipType(clipType);
    const canSelectTimeRange = isDesktop && isVideo;

    useEffect(
      () =>
        registerCommentInputFocusHandler('panel', () => {
          commentRef.current?.focus();
        }),
      [registerCommentInputFocusHandler],
    );

    const handleEnableTimestampsClick = useCallback(
      (enable: boolean) => {
        setShouldEnableCommentTimestamps(enable);

        if (enable) {
          if (!hasVideoTimestampFlag) {
            videoElement?.pause();
          }
        } else {
          setCommentRange(null);
          setCommentRangeEnabled(false);
        }
      },
      [hasVideoTimestampFlag, setCommentRange, setCommentRangeEnabled, setShouldEnableCommentTimestamps, videoElement],
    );

    const { timeMode, selectTimeMode } = useCommentTimeMode({
      canSelectTimeRange,
      shouldEnableTimestamps,
      setShouldEnableTimestamps: handleEnableTimestampsClick,
    });

    const handleFocus = useCallback(() => {
      if (!shouldEnableTimestamps) {
        return;
      }

      if (canSelectTimeRange) {
        enableCommentRange();
        return;
      }

      if (isDesktop && isTimestampableClip) {
        captureCommentTimestamp();
      }
    }, [
      canSelectTimeRange,
      captureCommentTimestamp,
      enableCommentRange,
      isDesktop,
      isTimestampableClip,
      shouldEnableTimestamps,
    ]);

    const formattedTimestamp = formatCommentTimeLabel({ commentRange, currentTime: capturedTimestamp });

    const handleCommentSubmit = useCallback(
      (comment: string) => {
        const shouldAttachTime = isDesktop && isTimestampableClip && shouldEnableTimestamps;
        const isRange = !!commentRange && commentRange.startTime !== commentRange.endTime;

        if (shouldAttachTime && canSelectTimeRange && isRange) {
          onSubmit({
            comment,
            timestamp: Math.min(commentRange.startTime, commentRange.endTime),
            endTimestamp: Math.max(commentRange.startTime, commentRange.endTime),
          });
        } else if (shouldAttachTime) {
          onSubmit({
            comment,
            timestamp: canSelectTimeRange ? (commentRange?.startTime ?? capturedTimestamp) : capturedTimestamp,
          });
        } else {
          onSubmit({ comment, timestamp: null });
        }

        if (commentRange) {
          setCommentRange(null);
          setCommentRangeEnabled(false);
        }

        if (isAnnotationsEnabled) {
          hideAnnotations();
        }
      },
      [
        hideAnnotations,
        isAnnotationsEnabled,
        isDesktop,
        isTimestampableClip,
        canSelectTimeRange,
        onSubmit,
        shouldEnableTimestamps,
        commentRange,
        capturedTimestamp,
        setCommentRange,
        setCommentRangeEnabled,
      ],
    );

    return (
      <DiscussionForm
        data-testid="ADD_DISCUSSION_COMMENT_INPUT"
        comment={comment}
        hasCancelButton={hasCancelButton}
        isLoadingMentions={isLoadingMentions}
        mentionsList={mentionsList}
        onCancel={onCancel}
        onSubmit={handleCommentSubmit}
        onFocus={handleFocus}
        placeholder={placeholder}
        shouldShowMentionsButton={shouldShowMentionsButton}
        hasInternalAvatar={hasInternalAvatar}
        shouldShowCancelOnDraft={false}
        ref={commentRef}
        {...(isVideo && {
          shouldEnableTimestamps,
          setShouldEnableTimestamps: handleEnableTimestampsClick,
          formattedTimestamp,
          shouldDisableTimestampsAndAnnotations: noPreviewInVisualizer,
          shouldBlurOnSubmit: true,
          timeMode,
          onSelectTimeMode: selectTimeMode,
          canSelectTimeRange,
        })}
        leftActions={<NewDiscussionActions canAnnotateClip={canAnnotateClip} onEnterCommentMode={onEnterCommentMode} />}
        helperActions={
          canAnnotateClip && isAnnotationsEnabled ? (
            <div className="rounded-6 ads:bg-raised bg-gray-2 p-1 backdrop-blur-sm">
              <AnnotationToolSelector />
            </div>
          ) : undefined
        }
      />
    );
  },
);

InitialCommentInput.displayName = 'InitialCommentInput';
